import { gql } from '@apollo/client';

/**
 * GraphQL operations for Sentry tracking
 * 
 * This file contains all GraphQL queries and mutations related to Sentry tracking.
 * It serves as a single source of truth for Sentry GraphQL operations.
 */

// ===== QUERIES =====

/**
 * Get all Sentry tracking configurations
 * Used in SentryTrackingManagement.tsx
 */
export const GET_ALL_SENTRY_TRACKING = gql`
  query GetAllSentryTracking {
    sentryTrackingConfigurations {
      id
      entityId
      entityType
      trackingLevel
      isActive
      startDate
      endDate
      createdAt
      updatedAt
      entityName
      entityEmail
      entityRole
      accountId
      accountName
      schoolId
      schoolName
    }
  }
`;

/**
 * Get Sentry tracking configuration for a specific entity
 * Used in SentryTrackingModal.tsx and SentryContext.tsx
 */
export const GET_ENTITY_SENTRY_TRACKING = gql`
  query GetEntitySentryTracking($entityId: String!, $entityType: EntityType!) {
    sentryTrackingByEntity(entityId: $entityId, entityType: $entityType) {
      id
      entityId
      entityType
      trackingLevel
      isActive
      startDate
      endDate
      entityName
      entityEmail
      entityRole
      accountId
      accountName
      schoolId
      schoolName
    }
  }
`;

/**
 * Check if a user belongs to a tracked account or school
 * Used in SentryContext.tsx
 */
export const CHECK_ENTITY_TRACKING = gql`
  query CheckEntityTracking($userId: String!) {
    checkEntityTracking(userId: $userId) {
      isTracked
      trackingLevel
      entityType
      entityId
      entityName
    }
  }
`;

/**
 * Get all users
 * Used in SentryTrackingManagement.tsx
 */
export const GET_USERS = gql`
  query GetUsers($input: SearchUsersInput!) {
    users(input: $input) {
      id
      email
      firstName
      lastName
      role
      customer {
        id
        name
      }
      schools {
        id
        name
      }
    }
  }
`;

/**
 * Get all accounts
 * Used in SentryTrackingManagement.tsx
 */
export const GET_ACCOUNTS = gql`
  query GetAccounts {
    customers {
      id
      name
    }
  }
`;

/**
 * Get all schools
 * Used in SentryTrackingManagement.tsx
 */
export const GET_SCHOOLS = gql`
  query GetSchools {
    schools {
      id
      name
    }
  }
`;

// ===== MUTATIONS =====

/**
 * Add Sentry tracking for an entity
 * Used in SentryTrackingModal.tsx and SentryTrackingManagement.tsx
 */
export const ADD_SENTRY_TRACKING = gql`
  mutation AddSentryTracking($input: SentryTrackingInput!) {
    addSentryTracking(input: $input) {
      id
      entityId
      entityType
      trackingLevel
      isActive
      startDate
      endDate
      entityName
      entityEmail
      entityRole
      accountId
      accountName
      schoolId
      schoolName
    }
  }
`;

/**
 * Update Sentry tracking for an entity
 * Used in SentryTrackingManagement.tsx
 */
export const UPDATE_SENTRY_TRACKING = gql`
  mutation UpdateSentryTracking($input: UpdateSentryTrackingInput!) {
    updateSentryTracking(input: $input) {
      id
      entityId
      entityType
      trackingLevel
      isActive
      startDate
      endDate
    }
  }
`;

/**
 * Remove Sentry tracking for an entity
 * Used in SentryTrackingModal.tsx and SentryTrackingManagement.tsx
 */
export const REMOVE_SENTRY_TRACKING = gql`
  mutation RemoveSentryTracking($id: ID!) {
    removeSentryTracking(id: $id)
  }
`;
